/* eslint-disable import/order */
import React, { Fragment } from 'react';
import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import { bool, func, number, shape, string } from 'prop-types';
import * as R from 'ramda';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

// styles
// Components
import { ChevronRightRounded } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { ProductIndicatorBadge } from 'common';
import { isAuthSequencingUpdatesEnabled } from 'feature/loading-management/utils/const';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';

// Helpers
import { getSelectionURI } from 'utils/helpers/url';
import { camelCaseToSentenceCase, pluralize } from 'feature/loading-management/utils/format';

const ListItemContainer = styled(Link, {
  shouldForwardProp: prop => !['isSelected', 'showAuthSequencingEnhancedUI'].includes(prop),
})(({ isSelected, showAuthSequencingEnhancedUI, theme }) => ({
  '&:hover': {
    '& [data-selector-chip-count=true], & .MuiChip-root': {
      backgroundColor: theme.palette.primary.light,
    },
    backgroundColor: theme.palette.secondary.light,
  },
  backgroundColor: `${isSelected ? theme.palette.secondary.light : 'transparent'}`,
  borderBottom: `1px solid ${theme.palette.surface.swimlane}`,
  display: 'grid',
  gridAutoRows: '1fr',
  gridRowGap: showAuthSequencingEnhancedUI ? 2 : 4,
  gridTemplateColumns: '32px 1fr 40px',
  height: '100%',
  minHeight: showAuthSequencingEnhancedUI ? 56 : 'auto',
  padding: showAuthSequencingEnhancedUI
    ? theme.spacing(1, 2, 0.875, 2)
    : theme.spacing(1.5, 1, 2, 2),
  textDecoration: 'none',
}));

const Selector = props => {
  const {
    data: { selectorsData },
    index,
    selected,
    setMobileDrawerOpen,
    setSelected,
    style,
  } = props;

  const { locationCount, nameShort, proprietary } = selectorsData[index];

  const isSelected = R.equals(index, selected);
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { viewBy } = useParams();
  const dispatch = useDispatch();
  // Feature flag: REACT_APP_LD_SEQUENCING_AUTH_LIST
  const isAuthSequencingUpdatesFlagOn = isAuthSequencingUpdatesEnabled();

  const performSelection = () => {
    setSelected(nameShort);
    !lgUp && setMobileDrawerOpen(true);
  };

  const handleOnClick = () => {
    // Clicking the already-selected item shouldn't prompt — nothing changes.
    if (isSelected) {
      return;
    }

    const hasUnsavedChanges =
      typeof window !== 'undefined' && Boolean(window.__authSeqHasUnsavedChanges);

    if (!hasUnsavedChanges) {
      performSelection();
      return;
    }

    dispatch(
      openConfirmationDialog({
        cancelAction: () => {},
        confirmAction: () => {
          performSelection();
        },
        header: 'Unsaved Changes',
        leftButtonActionType: 'confirm',
        leftButtonText: 'Continue',
        message:
          'Are you sure you want to leave this page? You have unsaved Authorization Sequence changes.',
        params: {},
        rightButtonActionType: 'cancel',
        rightButtonText: 'Keep Editing',
      })
    );
  };

  const chipNames =
    isAuthSequencingUpdatesFlagOn && viewBy === 'product'
      ? ['baseProductCount', 'customProductCount']
      : isAuthSequencingUpdatesFlagOn && viewBy === 'location'
      ? ['locationCount']
      : ['baseProductCount', 'customProductCount', 'locationCount'];

  const chips = chipNames.map(name => {
    const val = R.prop(name, selectorsData[index]);
    const displayName = pluralize(val, camelCaseToSentenceCase(name).slice(0, -6)).split(' ');
    displayName.shift();
    return (
      !!val && (
        <Fragment key={name}>
          <Box
            component="span"
            data-selector-chip-count
            sx={theme => ({
              ...theme.typography.caption,
              alignItems: 'center',
              backgroundColor: isSelected ? 'primary.light' : 'secondary.light',
              borderRadius: 0.5,
              display: 'flex',
              justifyContent: 'center',
              maxHeight: 20,
              mr: 1,
              pt: '1px',
              width: 25,
            })}
          >
            {val}
          </Box>
          <Typography component="span" display="block" sx={{ pt: '2px' }} variant="body2">
            {displayName.join(' ')}
          </Typography>
          {!locationCount && <div />}
        </Fragment>
      )
    );
  });

  return (
    <div style={style}>
      <ListItemContainer
        isSelected={isSelected}
        onClick={handleOnClick}
        showAuthSequencingEnhancedUI={isAuthSequencingUpdatesFlagOn}
        to={`../authorization-sequencing/${viewBy}/${getSelectionURI(selectorsData[index])}`}
      >
        <Typography
          sx={{
            alignSelf: 'center',
            color: 'text.primary',
            fontFamily: 'Gothic A1',
            fontSize: isAuthSequencingUpdatesFlagOn ? 16 : 18,
            fontWeight: 400,
            gridColumn: '1 / span 2',
            lineHeight: isAuthSequencingUpdatesFlagOn ? '24px' : undefined,
          }}
          variant="h6"
        >
          {nameShort}
        </Typography>
        {isAuthSequencingUpdatesFlagOn ? (
          <Box
            sx={{
              alignItems: 'center',
              alignSelf: 'center',
              display: 'flex',
              height: 40,
              justifyContent: 'center',
              justifySelf: 'end',
              width: 40,
            }}
          >
            <ChevronRightRounded
              sx={{
                color: 'text.secondary',
                fontSize: 24,
              }}
            />
          </Box>
        ) : (
          <ChevronRightRounded
            sx={{
              color: 'secondary.main',
              marginLeft: 'auto',
              size: 24,
            }}
          />
        )}
        {chips}
        {viewBy === 'product' && !isAuthSequencingUpdatesFlagOn && (
          <ProductIndicatorBadge proprietary={proprietary} />
        )}
      </ListItemContainer>
    </div>
  );
};
export default Selector;

Selector.propTypes = {
  data: shape({
    locationCount: number,
    nameShort: string,
    productCount: number,
    proprietary: bool,
  }).isRequired,
  index: number.isRequired,
  selected: number,
  setMobileDrawerOpen: func.isRequired,
  setSelected: func.isRequired,
  style: shape({}).isRequired,
};

Selector.defaultProps = {
  selected: undefined,
};
